import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { MatterArtifactSelectionActions } from '@/agent-v2/artifact-selection/store/slice'
import type { AgentV2MatterArtifactEditProposalReviewContext } from '@/agent-v2/artifact-edit-review/store/selectors'
import { AgentV2MatterArtifactEditProposalActions, AgentV2MatterArtifactEditProposalPhase } from '@/agent-v2/artifact-edit-review/store/slice'
import { selectAgentV2RunPhase } from '@/agent-v2/conversations/store/selectors'
import { AgentV2RunPhase } from '@/agent-v2/conversations/store/slice'
import { buildAgentV2ReferenceViewerSession } from '@/agent-v2/references/build-agent-v2-reference-viewer-session'
import { getAgentV2ExternalReferenceUrl } from '@/agent-v2/references/agent-v2-reference-opening'
import { selectAgentV2ReferencesByCitationId } from '@/agent-v2/references/store/selectors'
import { resolveStandaloneViewerUrlFromSession } from '@/standalone-reference-viewer/utils/resolve-standalone-viewer-url'
import type { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import AgentV2MatterArtifactEditReview from './AgentV2MatterArtifactEditReview'

type AgentV2PendingMatterArtifactEditProps = {
  context: AgentV2MatterArtifactEditProposalReviewContext
  conversationId: string
}

export default function AgentV2PendingMatterArtifactEdit({ context, conversationId }: AgentV2PendingMatterArtifactEditProps) {
  const { matterId } = useParams<{ matterId: string }>()
  const dispatch = useAppDispatch()
  const referencesByCitationId = useAppSelector((state: RootState) => selectAgentV2ReferencesByCitationId(state, { conversationId }))
  const runPhase = useAppSelector((state: RootState) => selectAgentV2RunPhase(state, { conversationId }))
  const { matterArtifactSelection, proposal } = context
  const accepting = proposal.phase === AgentV2MatterArtifactEditProposalPhase.ACCEPTING
  const canAccept = runPhase === AgentV2RunPhase.IDLE && !accepting
  const handleReject = useCallback(() => {
    dispatch(
      AgentV2MatterArtifactEditProposalActions.proposalRejected({
        conversationId,
        proposalId: proposal.proposalId,
      })
    )
    dispatch(MatterArtifactSelectionActions.clearMatterArtifactSelection({ conversationId }))
  }, [conversationId, dispatch, proposal.proposalId])

  useEffect(() => {
    // A proposal can arrive before the response finishes. Match the original editor by
    // enabling clickaway only after the review is actionable, never during generation.
    if (!canAccept) return

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target
      if (target instanceof Element && target.closest('[data-agent-v2-artifact-edit-review-root]')) return

      handleReject()
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      handleReject()
    }

    document.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [canAccept, handleReject])

  const handleCitationClick = useCallback(
    (citationId: string) => {
      const reference = referencesByCitationId[citationId]
      if (!reference) return
      const externalUrl = getAgentV2ExternalReferenceUrl(reference)
      if (externalUrl) {
        window.open(externalUrl, '_blank', 'noopener,noreferrer')
        return
      }

      const session = buildAgentV2ReferenceViewerSession(reference)
      const viewerUrl = resolveStandaloneViewerUrlFromSession(session, {
        conversationId,
        messageId: reference.turn_id,
        matterId,
      })
      if (!viewerUrl) return

      window.open(viewerUrl, '_blank', 'noopener,noreferrer')
    },
    [conversationId, matterId, referencesByCitationId]
  )

  return (
    <div className="pointer-events-none relative flex h-full flex-col justify-center" data-agent-v2-artifact-edit-review-root>
      <AgentV2MatterArtifactEditReview
        acceptingOperation={proposal.acceptingOperation}
        canAccept={canAccept}
        onAccept={(operation) =>
          dispatch(
            AgentV2MatterArtifactEditProposalActions.acceptanceRequested({
              conversationId,
              operation,
              proposalId: proposal.proposalId,
            })
          )
        }
        onCitationClick={handleCitationClick}
        onReject={handleReject}
        referencesByCitationId={referencesByCitationId}
        replacementMarkdown={proposal.replacementMarkdown}
        selectedMarkdown={matterArtifactSelection.selected_markdown}
      />
    </div>
  )
}
